import React, { useEffect, useMemo, useState } from "react";
import { transformDataToRows } from "./ContactHistoryTable.helpers";
import { usePutContactHistoryFilterQuery } from "../../../app/useGeneratedApi";
import { ContactHistoryTableProps } from "./ContactHistoryTable.types";
import { useRefreshRegister } from "../../../components/RefreshProvider";

export const useContactHistoryTable = ({
  filters,
}: ContactHistoryTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState<string>("day");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const queryParams = useMemo(
    () => ({
      page,
      size: rowsPerPage,
      sort: [`${sortBy},${sortOrder.toUpperCase()}`],
    }),
    [page, rowsPerPage, sortBy, sortOrder]
  );

  const {
    data,
    isLoading,
    refetch: refetchHistoryData,
  } = usePutContactHistoryFilterQuery(filters, queryParams, {
    keepPreviousData: true,
  });

  const tableRows = useMemo(() => {
    if (!data?.content) return [];
    return transformDataToRows(data.content);
  }, [data?.content]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (newSortBy: string) => {
    const newSortOrder =
      sortBy === newSortBy && sortOrder === "desc" ? "asc" : "desc";
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(0);
  };

  const currentSort = useMemo(
    () => ({
      order: sortOrder,
      field: sortBy,
    }),
    [sortBy, sortOrder]
  );

  useRefreshRegister(async () => {
    await refetchHistoryData();
  });

  const calculateTotalElements = useMemo(() => {
    const currentPageRecords = data?.content?.length ?? 0;
    if (currentPageRecords < rowsPerPage) {
      return page * rowsPerPage + currentPageRecords;
    }
    return (page + 1) * rowsPerPage + 1;
  }, [data?.content?.length, page, rowsPerPage]);

  return {
    handleSort,
    handleChangeRowsPerPage,
    handleChangePage,
    tableRows,
    isLoading,
    totalElements: calculateTotalElements,
    currentSort,
    page,
    rowsPerPage,
  };
};
